/**
 * Pure-logic primitives for the DraftScene flip-card surface (PR 2 of
 * the draft card refresh + gender / caughtAt plan).
 *
 * Node-testable in vitest. The Phaser/DOM shell in DraftScene.ts
 * imports + consumes these. Mirrors the established
 * dialogueOverlayLogic.ts / dialogueOverlay.ts split.
 *
 * Five exports:
 *   - ivBarCells(iv, cellCount)        — compact IV bar fill formula
 *   - shouldAutoShrinkName(name)       — auto-shrink DM Sans 20→16 px
 *   - genderGlyph(g)                   — ♂ / ♀ / em-dash
 *   - genderColorRgba(g)               — neutral RGBA, NOT type tokens
 *   - createSeenCardsTracker()         — one-shot-per-card-per-session
 */
import type { Gender } from '../state/runState';

/**
 * Compute how many cells of an IV bar fill given an IV value 0-31 and
 * a target bar resolution (e.g., 10 for the compact BACK-card variant,
 * 16 for the canonical DESIGN.md spec).
 *
 *   iv === 0    → 0 cells (empty bar — visible difference from iv=1)
 *   iv === 31   → cellCount (full bar — perfect-IV cells gate)
 *   otherwise   → ceil((iv / 31) * cellCount)
 *
 * Design doc Premise 2 locks `ceil` so 1 IV always shows at least one
 * cell — the player can distinguish "rolled a low IV" from "rolled
 * zero." Without ceil, iv=1 with cellCount=10 would round to 0 cells
 * (1/31 * 10 = 0.32) and erase the distinction.
 */
export function ivBarCells(iv: number, cellCount: number): number {
  if (iv === 0) return 0;
  return Math.ceil((iv / 31) * cellCount);
}

/**
 * True when the bare species displayName should render in the smaller
 * H3 variant instead of the default H2 to avoid overflowing the
 * available header width.
 *
 * Threshold is `length >= 9`. The roomy + spacious container tiers
 * render names at 21-22 px DM Sans 700; even 9-char names like
 * Bulbasaur / Charizard / Pidgeotto / Weedle (no — Weedle is 6) get
 * close to the header budget once gender glyph + star margin are
 * subtracted. Bumped from `>= 10` to `>= 9` after Bulbasaur shipped
 * truncated as "Bulbas…" at spacious. Gender glyph renders
 * separately and is NOT counted in the length.
 */
export function shouldAutoShrinkName(name: string): boolean {
  return name.length >= 9;
}

/**
 * Display glyph for a gender. ♂ + ♀ are cartridge-canonical Unicode
 * (U+2642 / U+2640); em-dash signals genderless.
 */
export function genderGlyph(g: Gender): string {
  if (g === 'male') return '♂'; // ♂
  if (g === 'female') return '♀'; // ♀
  return '—'; // — em-dash
}

/**
 * Color for the gender glyph. Design doc Premise 4 locks NEUTRAL RGBA
 * values (NOT the `--info-blue` / `--type-fairy` design tokens) —
 * gender is a CREATURE IDENTITY ATTRIBUTE, distinct from both state
 * color (--info-blue is reserved for info) and type identity color
 * (--type-fairy is a TYPE, not a gender). The RGBA values visually
 * match the cartridge blue/pink convention without claiming a design
 * token.
 *
 *   male       → rgba(75, 141, 255, 0.70)   (near-info-blue, neutral)
 *   female     → rgba(255, 147, 200, 0.70)  (near-fairy-pink, neutral)
 *   genderless → --text-tertiary            (em-dash via CSS var)
 */
export function genderColorRgba(g: Gender): string {
  if (g === 'male') return 'rgba(75, 141, 255, 0.70)';
  if (g === 'female') return 'rgba(255, 147, 200, 0.70)';
  return 'var(--text-tertiary)';
}

export interface SeenCardsTracker {
  /** Records that the FRONT of card `id` has been seen and returns true
   *  IFF this is the first time. Subsequent calls for the same id
   *  return false. Drives the egg-move 200 ms type-color glow burst
   *  (Premise 1) — fires only on first FRONT render per session. */
  seenFront(id: string): boolean;
  /** Records that the BACK of card `id` has been seen and returns true
   *  IFF this is the first time. Subsequent calls for the same id
   *  return false. Drives the perfect-IV cell 200 ms type-color glow
   *  burst (Premise 2) — fires only on first FRONT→BACK flip per
   *  session. */
  seenBack(id: string): boolean;
}

/**
 * Factory for a one-shot-per-card-per-face tracker. INSTANCE-scoped,
 * NOT module-scoped — each DraftScene mount gets a fresh tracker so
 * the bursts re-fire after a Reset Run or a subsequent draft modal.
 *
 * Two separate Sets so FRONT first-render and BACK first-flip
 * one-shots are independent: a card can fire the FRONT egg-move burst
 * then flip to BACK and fire the perfect-IV burst without one
 * suppressing the other.
 */
export function createSeenCardsTracker(): SeenCardsTracker {
  const seenFrontIds = new Set<string>();
  const seenBackIds = new Set<string>();
  return {
    seenFront(id: string): boolean {
      if (seenFrontIds.has(id)) return false;
      seenFrontIds.add(id);
      return true;
    },
    seenBack(id: string): boolean {
      if (seenBackIds.has(id)) return false;
      seenBackIds.add(id);
      return true;
    },
  };
}
